import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import {
  CommerceCatalogWorkspace,
  DescriptionText,
  HorizontalStack,
  PaneFooter,
  PaneHeader,
  PaneSection,
  PropertyGrid,
  PropertyItem,
  SectionSeparator,
  Surface,
  VerticalStack,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  Checkbox,
  RegistryStatus,
  type ColumnDef,
  type ColumnFiltersState,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';
import { useScope } from '@workspace/scope-context';
import { useNavigation } from '@/context/NavigationContext';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const RESELLER_ENTITLEMENT_SORT_FIELDS = ['created_at', 'name', 'resolved_commission_plan_id', 'resolved_pricebook_id', 'status', 'store_id', 'updated_at'] as const;
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  CommerceBreadcrumbHeader,
} from '@/views/commerce/catalogHierarchyShared';
import {
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  listResellerEntitlements,
  type ResellerEntitlement,
} from '@/lib/commerce';

function resellerAccessLabel(item: ResellerEntitlement): string {
  return `${item.access.kind}${item.access.id ? `:${item.access.id}` : ''}`;
}

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return 'success';
    case 'draft':
      return 'warning';
    case 'archived':
    case 'inactive':
      return 'muted';
    default:
      return 'default';
  }
}

export default function ResellerEntitlementsPage() {
  const { activeTenant } = useScope();
  const navigation = useNavigation();
  const [entitlements, setEntitlements] = useState<ResellerEntitlement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const query = useCommerceRegistryQuery({
    facetKeys: ['status'],
    facetValues: { status: ['active'] },
    sortFields: RESELLER_ENTITLEMENT_SORT_FIELDS,
  });
  const searchQuery = query.search;
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);
  const [totalEntitlements, setTotalEntitlements] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const requestSequence = useRef(0);
  const [selectedDetail, setSelectedDetail] = useState<ResellerEntitlement | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const columnFilters = useMemo<ColumnFiltersState>(() => (
    query.facets.status.length > 0 ? [{ id: 'status', value: query.facets.status }] : []
  ), [query.facets.status]);

  const loadData = async () => {
    const requestId = ++requestSequence.current;
    if (!activeTenant?.id) {
      return;
    }
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    try {
      const result = await listResellerEntitlements({
        search: searchQuery || undefined,
        status: query.facets.status,
        sort: query.sort,
        include_facets: ['status'],
        ...toLimitOffset(query.pagination),
      });
      if (requestId !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(result.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setEntitlements(result.items);
      setTotalEntitlements(result.total_count);
      setStatusCounts(Object.fromEntries((result.facets.status ?? []).map(bucket => [bucket.value, bucket.count])));
      setSelectedDetail(current => (
        current && result.items.some(item => item.entitlement_id === current.entitlement_id) ? current : null
      ));
    } catch (err) {
      if (requestId !== requestSequence.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load reseller entitlements.'));
      }
      setEntitlements([]);
      setTotalEntitlements(0);
      setStatusCounts({});
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeTenant?.id) {
      setEntitlements([]);
      setTotalEntitlements(0);
      setStatusCounts({});
      setLoading(false);
      setError(null);
      setAccessDeniedStatus(null);
      setSelectedDetail(null);
      return;
    }
    void loadData();
  }, [activeTenant?.id, query.facets.status, query.pagination, query.sort, searchQuery]);

  const columns = useMemo<ColumnDef<ResellerEntitlement>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: 'name',
      accessorKey: 'name',
      size: 250,
      header: 'Entitlement',
      cell: ({ row }) => {
        const item = row.original;
        return (
          <button
            type="button"
            onClick={() => setSelectedDetail(item)}
            className="group min-w-0 text-left"
            aria-label={`Inspect ${item.name}`}
          >
            <p className="text-sm font-black text-foreground transition-colors group-hover:text-primary">
              {item.name}
            </p>
            <DescriptionText className="line-clamp-1 text-[11px]">
              Access {resellerAccessLabel(item)} • Publication {item.storefront_key || item.catalog_key || item.publication_id}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      accessorKey: 'status',
      size: 100,
      header: 'Status',
      cell: ({ row }) => (
        <RegistryStatus label={row.original.status} variant={statusVariant(row.original.status)} />
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: 'store_id',
      accessorKey: 'store_id',
      size: 150,
      header: 'Store',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.store_id || 'Tenant level'}</span>
      ),
    },
    {
      id: 'resolved_pricebook_id',
      accessorKey: 'resolved_pricebook_id',
      size: 150,
      header: 'Resolved Pricebook',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.resolved_pricebook_id || 'Not resolved'}</span>
      ),
    },
    {
      id: 'resolved_commission_plan_id',
      accessorKey: 'resolved_commission_plan_id',
      size: 150,
      header: 'Resolved Commission',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.resolved_commission_plan_id || 'Not resolved'}</span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 80,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <HorizontalStack gap={2} className="justify-end shrink-0">
            <button
              type="button"
              onClick={() => setSelectedDetail(item)}
              aria-label={`Inspect ${item.name}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            >
              <Search className="h-3 w-3" />
            </button>
          </HorizontalStack>
        );
      },
    },
  ], []);
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: RESELLER_ENTITLEMENT_SORT_FIELDS, registry: 'reseller-entitlements' });

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    activeSectionId: 'reseller_entitlements',
    searchQuery,
    searchPlaceholder: 'Search entitlements...',
    onSearchChange: query.setSearch,
    error,
    blockingReason: 'Your current role does not have permission to inspect reseller entitlements.',
    onSelectSection: sectionId => {
      if (sectionId === 'reseller_entitlements') {
        navigation.navigate('/reseller/entitlements');
        return;
      }
      if (sectionId === 'linked_sellers') {
        navigation.navigate('/reseller/linked-sellers');
        return;
      }
      if (sectionId === 'carts') {
        navigation.navigate('/reseller/carts');
        return;
      }
      if (sectionId === 'orders') {
        navigation.navigate('/reseller/orders');
        return;
      }
      if (sectionId === 'commission_ledger') {
        navigation.navigate('/reseller/commission-ledger');
        return;
      }
      navigation.navigate(buildCommerceSectionRoute(sectionId));
    },
    primaryAction: {
      id: 'refresh-entitlements',
      label: 'Refresh',
      onAction: () => void loadData(),
      disabled: loading,
    },
  });

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      padded={false}
      titleContent={
        <CommerceBreadcrumbHeader
          breadcrumbs={[{ label: 'Entitlements' }]}
          title="Entitlements"
          description="Inspect the publications and commercial defaults your active tenant can currently resell."
        />
      }
    >
      <RegistryWorkspace>
        <RegistryTableSurface>
          <DataTable
            mode="manual"
            columns={columns}
            data={entitlements}
            sorting={sorting}
            onSortingChange={updater => {
              const next = typeof updater === 'function' ? updater(sorting) : updater;
              query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
            }}
            globalFilter={searchQuery}
            columnFilters={columnFilters}
            onColumnFiltersChange={updater => {
              const next = typeof updater === 'function' ? updater(columnFilters) : updater;
              const filter = next.find(item => item.id === 'status');
              query.setFacet('status', Array.isArray(filter?.value) ? filter.value.map(String) : []);
            }}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            pagination={query.pagination}
            onPaginationChange={query.onPaginationChange}
            manualPagination
            totalRowCount={totalEntitlements}
            loading={loading}
            className="border-none rounded-none"
            paginationClassName="px-0 py-0"
            showPagination
            emptyState={
              <div className="px-0 py-12 text-center">
                <p className="text-sm font-black text-foreground">No Reseller Entitlements</p>
                <DescriptionText className="mt-1 text-xs">
                  {searchQuery || query.facets.status.length > 0
                    ? 'No reseller entitlements matched the current search or filters.'
                    : 'No resolved selling rights were returned for the active reseller tenant.'}
                </DescriptionText>
                {searchQuery || query.facets.status.length > 0 ? (
                  <button type="button" onClick={query.clear} className="mt-3 text-xs font-semibold text-primary hover:underline">
                    Reset search and filters
                  </button>
                ) : null}
              </div>
            }
            renderToolbar={table => (
              <DataTableToolbar
                table={table}
                searchValue={searchQuery}
                onSearchChange={query.setSearch}
                searchPlaceholder="Search all entitlements..."
                resultCount={totalEntitlements}
                className="border-b border-border/40 bg-transparent px-0 py-0"
                filters={
                  <DataTableFacetedFilter
                    title="Status"
                    selectedValues={new Set(query.facets.status)}
                    onFilterChange={values => query.setFacet('status', Array.from(values))}
                    counts={statusCounts}
                    options={[{ label: 'Active', value: 'active' }]}
                  />
                }
              />
            )}
          />
        </RegistryTableSurface>
      </RegistryWorkspace>

      {selectedDetail ? (
        <div className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-background shadow-2xl duration-300 animate-in slide-in-from-right sm:max-w-2xl">
          <PaneHeader
            title="Reseller Entitlement Detail"
            subtitle={`Entitlement ${selectedDetail.entitlement_id}`}
            onClear={() => setSelectedDetail(null)}
          />
          <div className="flex-1 overflow-y-auto">
            <PaneSection label="Resolved Selling Context">
              <PropertyGrid cols={2}>
                <PropertyItem label="Entitlement ID" value={selectedDetail.entitlement_id} />
                <PropertyItem label="Publication ID" value={selectedDetail.publication_id} />
                <PropertyItem label="Entitlement" value={selectedDetail.name} />
                <PropertyItem label="Selling Tenant" value={selectedDetail.selling_tenant_id} />
                <PropertyItem label="Catalog Owner" value={selectedDetail.catalog_owner_tenant_id} />
                <PropertyItem label="Catalog" value={selectedDetail.catalog_key || selectedDetail.catalog_id} />
                <PropertyItem label="Storefront" value={selectedDetail.storefront_key || 'Not linked'} />
                <PropertyItem label="Access" value={resellerAccessLabel(selectedDetail)} />
                <PropertyItem label="Store" value={selectedDetail.store_id || 'Tenant level'} />
                <PropertyItem label="Agent" value={selectedDetail.agent_user_id || 'Not scoped'} />
                <PropertyItem label="Resolved Pricebook" value={selectedDetail.resolved_pricebook_id || 'Not resolved'} />
                <PropertyItem label="Resolved Commission" value={selectedDetail.resolved_commission_plan_id || 'Not resolved'} />
              </PropertyGrid>
            </PaneSection>
            <SectionSeparator />
            <PaneSection label="Publication Defaults">
              <PropertyGrid cols={2}>
                <PropertyItem label="Publication Scope" value={selectedDetail.publication_scope} />
                <PropertyItem label="Publication Status" value={selectedDetail.publication_status} />
                <PropertyItem label="Entitlement Status" value={selectedDetail.status} />
                <PropertyItem label="Default Pricebook" value={selectedDetail.default_pricebook_id || 'Not set'} />
                <PropertyItem label="Default Commission" value={selectedDetail.default_commission_plan_id || 'Not set'} />
              </PropertyGrid>
            </PaneSection>
            <SectionSeparator />
            <PaneSection label="Organization Context">
              <Surface className="border-border/60 p-4">
                <DescriptionText className="text-xs">
                  {(selectedDetail.organization_ids || []).length > 0
                    ? selectedDetail.organization_ids.join(', ')
                    : 'No organization grants were required for this resolved view.'}
                </DescriptionText>
              </Surface>
            </PaneSection>
          </div>
          <PaneFooter>
            <HorizontalStack className="justify-end">
              <button
                type="button"
                onClick={() => setSelectedDetail(null)}
                className="px-4 py-2 text-xs font-bold text-muted-foreground transition-all hover:text-foreground"
              >
                Close
              </button>
            </HorizontalStack>
          </PaneFooter>
        </div>
      ) : null}
    </CommerceCatalogWorkspace>
  );
}
